"use client";

import { ComparisonBody, type Section } from "./comparison-body";
import { ComparisonHead } from "./comparison-head";
import {
  type ComparisonItem,
  useComparisonColumns,
} from "./use-comparison-columns";

export type Schema = {
  sections: Section[];
};

const DESKTOP_COLUMN_COUNT = 3;
const MOBILE_COLUMN_COUNT = 2;

type ComparisonProperties = {
  /**
  Accessible description of the table, rendered visually hidden via a `<caption>`.
  */
  caption?: string;
  desktopNumber?: number | undefined;
  items: ComparisonItem[];
  /**
  Accessible label for the blank label-column header, rendered visually hidden.
  */
  labelColumnHeading: string;
  mobileNumber?: number | undefined;
  schema: Schema;
};

export const Comparison = (properties: ComparisonProperties) => (
  <ComparisonInner
    key={[
      ...properties.schema.sections.map((section) => section.id),
      ...properties.items.map((item) => item.id),
    ].join("|")}
    {...properties}
  />
);

const ComparisonInner = ({
  caption,
  desktopNumber = DESKTOP_COLUMN_COUNT,
  items,
  labelColumnHeading,
  mobileNumber = MOBILE_COLUMN_COUNT,
  schema,
}: ComparisonProperties) => {
  const { isMobile, onColumnChange, showColumnPickers, visibleColumns } =
    useComparisonColumns(items, desktopNumber, mobileNumber);
  const isShowLabelColumn = !isMobile;

  return (
    <table
      className="
        w-full table-fixed

        [&>tbody:first-of-type>tr:first-of-type>*:first-child]:rounded-ss-2xl

        [&>tbody:first-of-type>tr:first-of-type>*:last-child]:rounded-se-2xl

        [&>tbody:last-of-type>tr:last-of-type>*:first-child]:rounded-es-2xl

        [&>tbody:last-of-type>tr:last-of-type>*:last-child]:rounded-ee-2xl
      "
      data-fs-element="comparison-table"
    >
      {caption && <caption className="sr-only">{caption}</caption>}
      <colgroup>
        {isShowLabelColumn && <col className="w-[30%]" />}

        {visibleColumns.map(({ columnKey }) => (
          <col key={columnKey} />
        ))}
      </colgroup>

      <ComparisonHead
        items={items}
        labelColumnHeading={labelColumnHeading}
        onColumnChange={onColumnChange}
        showColumnPickers={showColumnPickers}
        showLabelColumn={isShowLabelColumn}
        visibleColumns={visibleColumns}
      />

      {schema.sections.map((section) => (
        <ComparisonBody
          key={section.id}
          section={section}
          showLabelColumn={isShowLabelColumn}
          visibleColumns={visibleColumns}
        />
      ))}
    </table>
  );
};
